import type { CurrenciesResponse, TenantCurrency } from '@erp/shared';
import { useEffect } from 'react';
import { create } from 'zustand';

import { api } from './api.js';
import { useAuthStore } from './authStore.js';
import { setCurrencyDecimals } from './money.js';

interface CurrenciesState {
  currencies: TenantCurrency[];
  /** The shop's own currency as the server has it; null until it answers. */
  baseCurrencyCode: string | null;
  loaded: boolean;
  load: () => Promise<void>;
  /** Re-fetch even if already loaded — after Settings → Currencies changes the list. */
  reload: () => Promise<void>;
}

// Shared in-flight promise so the many concurrent callers (AppLayout, guards,
// every CurrencySelect mount) trigger at most one GET /currencies at a time.
let inflight: Promise<void> | null = null;

/**
 * The currencies this tenant USES (GET /currencies → only the active ones),
 * loaded once + shared. Every CurrencySelect reads it.
 */
export const useCurrenciesStore = create<CurrenciesState>((set, get) => {
  const fetchAll = (): Promise<void> => {
    if (inflight) return inflight;
    inflight = (async () => {
      try {
        const r = await api().get<CurrenciesResponse>('/currencies');
        // Push per-currency exponents into the formatter registry so formatMoney
        // honors the backend-configured decimals (e.g. IQD → 0) instead of CLDR.
        // `decimals` covers the WHOLE catalogue, not just the active subset, so
        // an amount stored in a deactivated currency still formats correctly.
        setCurrencyDecimals(r.data.decimals ?? r.data.items);
        // Only latch `loaded` on success — a transient failure must NOT stick an
        // empty list for the whole session (that renders IQD with wrong decimals);
        // leaving loaded=false lets a later load() retry.
        set({ currencies: r.data.items, baseCurrencyCode: r.data.baseCurrencyCode ?? null, loaded: true });
      } catch {
        // Non-fatal — CurrencySelect falls back to the base + current value, and
        // the next load() call retries.
      } finally {
        inflight = null;
      }
    })();
    return inflight;
  };
  return {
    currencies: [],
    baseCurrencyCode: null,
    loaded: false,
    load: async () => {
      if (get().loaded) return;
      return fetchAll();
    },
    reload: fetchAll,
  };
});

/**
 * The shop's own currency — the other side of every exchange rate. The server's
 * answer (GET /currencies) wins; the session's copy stands in until it arrives.
 * Null while neither is known, and a screen that WRITES a rate must then wait
 * rather than guess: a guessed "USD" once saved "1 USD = 1,700 IQD" as
 * "1 IQD = 1,700 USD".
 */
export function useBaseCurrency(): string | null {
  const server = useCurrenciesStore((s) => s.baseCurrencyCode);
  const session = useAuthStore((s) => s.tenant?.baseCurrencyCode ?? null);
  useEffect(() => { void useCurrenciesStore.getState().load(); }, []);
  return server ?? session;
}
